import React, { ReactNode, useMemo, useState } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { components, MultiValue, OptionProps } from 'react-select';
import styled from 'styled-components';
import { useLazyQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import EntityPermissionsWidgetUser from '../EntityPermissionsWidgetUser';
import EntityPermissionsWidgetRoles from '../EntityPermissionsWidgetRoles';
import {
  EntityRole,
  IEntityPermissionFieldsFragment,
  IUserFieldsFragment,
  IUsersWithBasicInfoQuery,
  IUsersWithBasicInfoQueryVariables,
  UserState,
} from '../../../utils/apollo/graphqlSchema';
import {
  entityPermissionsWidgetSchema,
  IEntityPermissionsWidgetState,
  IEntityPermissionsWidgetUser,
  publicRoleOptions,
  userRoleOptions,
} from '../utils';
import { GET_USERS_WITH_BASIC_INFO } from '../../../utils/apollo/permission';
import { ReactSelectMenu } from '../../Dropdown/StyledReactSelect';
import Avatar from '../../Avatar';
import { useDebounce } from '../../../utils/hooks';
import { FieldLabel, VerticalField } from '../../Form';
import { SvgIcon } from '../../Icon';
import { floatingComponentStyles } from '../../../styles/shareableStyles';
import { getScrollBarStyles } from '../../../config/styles';

interface IProps {
  permission: IEntityPermissionFieldsFragment;
  onSubmit: (formData: IEntityPermissionsWidgetState) => Promise<void>;
  title?: string;
  showPublicRole?: boolean;
  shareLink?: ReactNode;
  disableRoleChange?: boolean;
}

const userStates = [UserState.Active];

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  width: 344px;
  padding: 12px;
  ${floatingComponentStyles};
`;
const UsersContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  max-height: 146px;
  overflow-y: auto;
  ${getScrollBarStyles(4)};
`;

const renderOptionItem = (
  props: OptionProps<IUserFieldsFragment, true, never>,
) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <Avatar picture={data.picture} size={16} />
      <div>{data.name}</div>
    </components.Option>
  );
};

const EntityPermissionsWidgetPopover = ({
  permission,
  onSubmit,
  title = 'Share',
  showPublicRole = true,
  shareLink,
  disableRoleChange = false,
}: IProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchQuery = useDebounce(searchTerm, 300);

  const { defaultPublicRole, roleAssignments } = permission;

  const [loadUserList, { called, data, loading: usersLoading }] = useLazyQuery<
    IUsersWithBasicInfoQuery,
    IUsersWithBasicInfoQueryVariables
  >(GET_USERS_WITH_BASIC_INFO, {
    variables: {
      pagination: {
        first: 50,
        after: 0,
      },
      term: debouncedSearchQuery,
      includes: [],
      userStates,
    },
  });

  const formMethods = useForm<IEntityPermissionsWidgetState>({
    resolver: zodResolver(entityPermissionsWidgetSchema),
    defaultValues: {
      defaultPublicRole,
      users: roleAssignments.reduce<IEntityPermissionsWidgetUser[]>(
        (acc, roleAssignment) => {
          roleAssignment.users.forEach((user) => {
            const userData = {
              id: user.id,
              name: user.name,
              picture: user.picture,
              role: roleAssignment.role,
            };

            // Owner should be always on top of the list
            if (roleAssignment.role === EntityRole.Owner) {
              acc.unshift(userData);
            } else {
              acc.push(userData);
            }
          });

          return acc;
        },
        [],
      ),
    },
  });

  const { control, getValues } = formMethods;

  const {
    fields: users,
    append: addUser,
    remove: removeUser,
  } = useFieldArray({
    name: 'users',
    keyName: 'arrayId',
    control,
  });

  const handleSubmit = async () => {
    await onSubmit(getValues());
  };

  const userMenuOptions: IUserFieldsFragment[] = useMemo(() => {
    const usersList = data?.users?.entities;

    if (!usersList?.length) {
      return [];
    }

    return usersList.filter(
      (user) => !users.some((formUser) => formUser.id === user.id),
    );
  }, [data, users]);

  const handleSelectUser = (newOptions: MultiValue<IUserFieldsFragment>) => {
    if (!newOptions?.length) {
      return;
    }

    const selectedUser = newOptions[newOptions.length - 1];

    addUser({
      id: selectedUser.id,
      name: selectedUser.name,
      picture: selectedUser.picture,
      role: EntityRole.Editor,
    });
    handleSubmit();
  };

  const handleLoadOptions = async () => {
    if (!called) {
      await loadUserList();
    }
  };

  const handleUserRoleChange = (
    field: ControllerRenderProps<
      IEntityPermissionsWidgetState,
      `users.${number}.role`
    >,
    value: string,
  ) => {
    field.onChange(value);
    handleSubmit();
  };

  const handlePublicRoleChange = (
    field: ControllerRenderProps<
      IEntityPermissionsWidgetState,
      'defaultPublicRole'
    >,
    value: string,
  ) => {
    field.onChange(value);
    handleSubmit();
  };

  const handleRemoveUser = (index: number) => {
    removeUser(index);
    handleSubmit();
  };

  return (
    <FormProvider {...formMethods}>
      <FormContainer>
        <VerticalField noMargin={true}>
          <FieldLabel>{title}</FieldLabel>
          <ReactSelectMenu<IUserFieldsFragment, true, never>
            onMenuOpen={handleLoadOptions}
            autoFocus={false}
            options={userMenuOptions}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            onChange={handleSelectUser}
            value={[]}
            isMulti={true}
            controlShouldRenderValue={false}
            isLoading={usersLoading}
            isSearchable={true}
            blurInputOnSelect={true}
            inputValue={searchTerm}
            onInputChange={setSearchTerm}
            placeholder={'Search users'}
            components={{
              Option: renderOptionItem,
            }}
            styles={{
              option: (base) => ({
                ...base,
                display: 'flex',
                columnGap: '4px',
              }),
            }}
          />
        </VerticalField>
        <FieldLabel>Who has access</FieldLabel>
        <UsersContainer>
          {users.map((user, userIndex) => (
            <EntityPermissionsWidgetUser
              key={user.arrayId}
              name={user.name}
              roundIcon={<Avatar picture={user.picture} size={24} />}
            >
              <Controller
                name={`users.${userIndex}.role`}
                control={control}
                render={({ field }) => (
                  <EntityPermissionsWidgetRoles
                    entityRole={field.value}
                    onChange={(value) => handleUserRoleChange(field, value)}
                    onRemove={() => handleRemoveUser(userIndex)}
                    options={userRoleOptions}
                    disabled={disableRoleChange}
                  />
                )}
              />
            </EntityPermissionsWidgetUser>
          ))}

          {showPublicRole && (
            <EntityPermissionsWidgetUser
              name={'Public'}
              roundIcon={
                <SvgIcon name="circle-users" width={24} height={24} isActive />
              }
            >
              <Controller
                name="defaultPublicRole"
                control={control}
                render={({ field }) => (
                  <EntityPermissionsWidgetRoles
                    entityRole={field.value}
                    onChange={(value) => handlePublicRoleChange(field, value)}
                    options={publicRoleOptions}
                    disabled={disableRoleChange}
                  />
                )}
              />
            </EntityPermissionsWidgetUser>
          )}
        </UsersContainer>
        {shareLink}
      </FormContainer>
    </FormProvider>
  );
};

export default EntityPermissionsWidgetPopover;
